import type { Step } from 'react-joyride';

/**
 * Guided tour for the Form Builder: list -> editor -> runtime list -> runtime form.
 *
 * Each page owns a short tour. The last step of a page can continue to the next
 * page; the hand-off is carried by a sessionStorage flag that the next page's
 * `TourButton` consumes when it mounts.
 *
 * Steps point at `data-tour="<name>"` attributes, so tour copy can change
 * without touching component structure. Steps whose target is missing or has no
 * size (e.g. no published forms yet, no fields yet) are skipped by the tour.
 */
export type TourId = 'list' | 'editor' | 'runtime' | 'dynamicList' | 'dynamicEditor';

export interface TourDefinition {
  steps: Step[];
  /** Next page in the flow, or undefined when this is the end. */
  next?: () => { id: TourId; route: string } | undefined;
}

const ACTIVE_KEY = 'fb-tour:active';
const seenKey = (id: TourId) => `fb-tour:seen:${id}`;

type StorageKind = 'local' | 'session';
const area = (kind: StorageKind) => (kind === 'local' ? window.localStorage : window.sessionStorage);
const store = {
  get(kind: StorageKind, key: string): string | null {
    try { return area(kind).getItem(key); } catch { return null; }
  },
  set(kind: StorageKind, key: string, value: string) {
    try { area(kind).setItem(key, value); } catch { /* storage unavailable */ }
  },
  remove(kind: StorageKind, key: string) {
    try { area(kind).removeItem(key); } catch { /* storage unavailable */ }
  },
};

export const tourSeen = (id: TourId) => store.get('local', seenKey(id)) === '1';
export const markTourSeen = (id: TourId) => store.set('local', seenKey(id), '1');
export const handOffTour = (id: TourId) => store.set('session', ACTIVE_KEY, id);
/**
 * True while a hand-off to `id` is pending. Read-only on purpose: React may run a
 * mount effect twice (StrictMode) or restart it, and a destructive read would let
 * the first run swallow the hand-off so the tour never starts on the new page.
 */
export const hasTourHandOff = (id: TourId) => store.get('session', ACTIVE_KEY) === id;
/** True (and consumed) when the previous page handed the tour over to `id`. */
export const consumeTourHandOff = (id: TourId) => {
  if (store.get('session', ACTIVE_KEY) !== id) return false;
  store.remove('session', ACTIVE_KEY);
  return true;
};

const t = (name: string) => `[data-tour="${name}"]`;

export const TOURS: Record<TourId, TourDefinition> = {
  list: {
    steps: [
      {
        target: t('list-header'),
        placement: 'bottom-start',
        title: 'Form Builder',
        content: 'Create forms, publish them, and let users fill them in. This tour covers the essentials.',
      },
      {
        target: t('list-new'),
        placement: 'left',
        title: 'New form',
        content: 'Click here to start a form. You will pick its sensor type, category and calibration type.',
      },
      {
        target: t('list-filters'),
        placement: 'bottom-start',
        title: 'Search and filter',
        content: 'Find a form by name or key. Filter by Draft, Published or Inactive (deleted).',
      },
      {
        target: t('list-card'),
        placement: 'top-start',
        title: 'Form card',
        content: 'Shows the form status and version. Edit changes the draft; the published version stays live until you publish again.',
      },
      {
        target: t('list-menu'),
        placement: 'left-start',
        title: 'More actions',
        content: 'Duplicate, view Version history (and roll back), see the Audit log, or delete. Deleted forms can be restored from Inactive.',
      },
      {
        target: t('list-runtime'),
        placement: 'top',
        title: 'Open the live form',
        content: 'The eye button opens the published form, exactly as users will see and submit it.',
      },
    ],
  },

  editor: {
    steps: [
      {
        target: t('editor-header'),
        placement: 'bottom-start',
        title: 'Workspace layout',
        content: 'Left: form settings and steps. Centre: fields of the current step. Right: settings of the selected field.',
      },
      {
        target: t('editor-details'),
        placement: 'right-start',
        title: '1 · Form details',
        content: 'Set the form name, the title users see, a description and the submit button label.',
      },
      {
        target: t('editor-steps'),
        placement: 'right-start',
        title: '2 · Steps',
        content: 'Split the form into steps. Add, reorder, duplicate or delete them, then click a step to edit its fields.',
      },
      {
        target: t('editor-review'),
        placement: 'right-start',
        title: '3 · Final review',
        content: 'The last page, where users check their answers. Set its title here; hide a field from it with "Show in final review" in the field settings.',
      },
      {
        target: t('editor-submission'),
        placement: 'right-start',
        title: '4 · Submission API',
        content: 'Choose where the data is sent on submit: method, endpoint and success message. Turn on "Send raw form values" to send answers as they are.',
      },
      {
        target: t('editor-field-actions'),
        placement: 'bottom-end',
        title: '5 · Add fields',
        content: 'Add a field to the current step. Types: input, select, radio, checkbox and file.',
      },
      {
        target: t('editor-dynamic'),
        placement: 'bottom-start',
        title: 'Runtime dynamic fields',
        content: 'Optional. Pick extra fields from the Dynamic fields library; they appear when users open this step.',
      },
      {
        target: t('editor-fields'),
        placement: 'top-start',
        title: '6 · Field list',
        content: 'Drag to reorder, move a field to another step, or delete it. Click a field to configure it.',
      },
      {
        target: t('editor-field-config'),
        placement: 'left-start',
        title: '7 · Field settings',
        content: 'Set label, key, type and options, plus validation and conditions (show, require or disable the field based on other answers).',
      },
      {
        target: t('editor-preview'),
        placement: 'bottom-end',
        title: '8 · Preview',
        content: 'Try the form with validation and conditions. Nothing is submitted from the preview.',
      },
      {
        target: t('editor-save'),
        placement: 'bottom-end',
        title: '9 · Save draft',
        content: 'Saves your work without affecting the live form. Save often.',
      },
      {
        target: t('editor-publish'),
        placement: 'bottom-end',
        title: '10 · Publish',
        content: 'Makes the saved draft the live version, with an optional release note. Save the draft first.',
      },
    ],
  },

  runtime: {
    steps: [
      {
        target: t('runtime-header'),
        placement: 'bottom-start',
        title: 'Live form',
        content: 'This is the published form. Submitting sends real data to the configured API.',
      },
      {
        target: t('runtime-steps'),
        placement: 'bottom-start',
        title: 'Progress',
        content: 'Shows the current step and the ones you have completed. Your answers are kept when you move between steps.',
      },
      {
        target: t('runtime-fields'),
        placement: 'top-start',
        title: 'Fill in the fields',
        content: 'Fields marked * are required. Some fields appear, change or load their options based on your earlier answers.',
      },
      {
        target: t('runtime-nav'),
        placement: 'top-end',
        title: 'Next, Review, Submit',
        content: 'Next checks the step and continues. Review only shows a summary; nothing is sent until you press Submit. If it fails, "Detailed Error" shows what the server reported.',
      },
    ],
  },

  dynamicList: {
    steps: [
      {
        target: t('dynamic-fields-header'),
        placement: 'bottom-start',
        title: 'Dynamic field libraries',
        content: 'A library holds the extra fields (for example readings) that forms show when a step opens. There is one per sensor type and category.',
      },
      {
        target: t('dynamic-fields-search'),
        placement: 'bottom',
        title: 'Search',
        content: 'Filter the list by sensor type or category.',
      },
      {
        target: t('dynamic-fields-row'),
        placement: 'bottom-start',
        title: 'Status',
        content: 'Live shows the published version. Draft means there are unpublished changes. Forms only use the published version.',
      },
      {
        target: t('dynamic-fields-actions'),
        placement: 'left',
        title: 'Actions',
        content: 'Define or Edit the fields, Preview what a form receives, open Versions to roll back, or Audit to see who changed what.',
      },
    ],
  },

  dynamicEditor: {
    steps: [
      {
        target: t('dynamic-fields-editor-header'),
        placement: 'bottom-start',
        title: 'One library',
        content: 'You are editing the fields for one sensor type and category. Saving creates a draft; users keep the live version until you publish.',
      },
      {
        target: t('dynamic-fields-add'),
        placement: 'right-start',
        title: '1 · Add entries',
        content: 'Field is a single field such as BOL or AOL. Temperature adds the temperature readings. Points adds the calibration points (one per library).',
      },
      {
        target: t('dynamic-fields-entries'),
        placement: 'right-start',
        title: '2 · Entry list',
        content: 'Click an entry to edit it. Use the arrows to reorder and the bin to remove. Entries marked "needs attention" must be fixed before saving.',
      },
      {
        target: t('dynamic-fields-entry-editor'),
        placement: 'left-start',
        title: '3 · Entry settings',
        content: 'Set the key, label, type and validation. Mark an entry inactive to keep it but hide it from forms.',
      },
      {
        target: t('dynamic-fields-preview'),
        placement: 'left-start',
        title: '4 · Preview',
        content: 'After publishing, enter a range to see the exact field keys a form receives.',
      },
      {
        target: t('dynamic-fields-save'),
        placement: 'bottom-end',
        title: '5 · Save draft',
        content: 'Saves your changes without affecting the live library.',
      },
      {
        target: t('dynamic-fields-publish'),
        placement: 'bottom-end',
        title: '6 · Publish',
        content: 'Makes the draft live for every form that uses it. Previous versions stay in history for rollback.',
      },
    ],
  },
};

const isVisible = (target: Step['target']) => {
  if (typeof target !== 'string') return !!target;
  const el = document.querySelector(target);
  if (!el) return false;
  const rect = el.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
};

/** Steps whose target is currently on screen. Call when the tour starts. */
export const availableSteps = (id: TourId): Step[] =>
  TOURS[id].steps.map((step) => ({ ...step, disableBeacon: true })).filter((step) => isVisible(step.target));

const sleep = (ms: number) => new Promise<void>((resolve) => window.setTimeout(resolve, ms));

/**
 * Opens a collapsed <details> section, then scrolls the target to the middle of
 * its scroll container(s) and waits for the scroll to settle. The tooltip is
 * only shown afterwards, so it never measures a target that is still moving.
 */
export async function revealStepTarget(step?: Step) {
  if (!step || typeof step.target !== 'string') return;
  const el = document.querySelector(step.target);
  if (!el) return;
  if (el instanceof HTMLDetailsElement && !el.open) {
    el.open = true;
    await sleep(60);
  }
  el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'smooth' });
  await sleep(450);
}

const COVER_ID = 'fb-tour-cover';
let coverSafety: number | undefined;

/** Fades a full-screen cover in so a page change during the tour is not a hard jump. */
export async function coverPage() {
  document.getElementById(COVER_ID)?.remove();
  const el = document.createElement('div');
  el.id = COVER_ID;
  el.setAttribute('aria-hidden', 'true');
  Object.assign(el.style, {
    position: 'fixed', inset: '0', background: '#f8fafc', opacity: '0', zIndex: '9999',
    transition: 'opacity 220ms ease',
  });
  document.body.appendChild(el);
  void el.offsetHeight; // commit opacity 0 so the transition runs
  el.style.opacity = '1';
  window.clearTimeout(coverSafety);
  coverSafety = window.setTimeout(uncoverPage, 8000); // never leave the app covered
  await sleep(240);
}

export function uncoverPage() {
  window.clearTimeout(coverSafety);
  const el = document.getElementById(COVER_ID);
  if (!el) return;
  el.style.transition = 'opacity 320ms ease';
  el.style.opacity = '0';
  window.setTimeout(() => el.remove(), 340);
}
